import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import type { components } from "@dolfin/api-admin";
import { useAuth } from "@/auth/AuthContext";
import { api } from "@/api/client";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { Button } from "@/components/ui/button";
import { useSaveBarSection } from "@/components/portal/SaveBar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn, dirtyRing } from "@/lib/utils";

type Options = components["schemas"]["InvoicePdfDisplayOptions"];
type Flag = keyof Options;
type Template = components["schemas"]["InvoicePdfTemplate"];

const TEMPLATES: Template[] = ["Classic", "Minimal", "Branded"];

const VAT_WARNING = "VAT invoices may need this. Check before hiding it.";

type FlagItem = { flag: Flag; label: string; warnWhenOff?: string };

// Separate per party, so a client can hide its own state but keep the customer's.
function addressFlags(party: "Sender" | "Customer", warn?: string): FlagItem[] {
  return [
    { flag: `show${party}AddressLine1`, label: "Line 1", warnWhenOff: warn },
    { flag: `show${party}AddressLine2`, label: "Line 2" },
    { flag: `show${party}City`, label: "Town or city", warnWhenOff: warn },
    { flag: `show${party}State`, label: "State or county" },
    { flag: `show${party}PostalCode`, label: "Postcode", warnWhenOff: warn },
    { flag: `show${party}Country`, label: "Country" },
  ];
}

const GROUPS: { title: string; flags: FlagItem[]; address?: FlagItem[] }[] = [
  {
    title: "Sender",
    flags: [
      { flag: "showLogo", label: "Logo" },
      { flag: "showSenderEmail", label: "Email" },
      { flag: "showSenderPhone", label: "Phone" },
      { flag: "showSenderWebsite", label: "Website" },
      { flag: "showSenderTaxNumber", label: "Tax number", warnWhenOff: VAT_WARNING },
      { flag: "showSenderRegistrationNumber", label: "Registration number" },
    ],
    address: addressFlags("Sender"),
  },
  {
    title: "Customer",
    flags: [
      { flag: "showCustomerTaxNumber", label: "Tax number" },
      { flag: "showNotes", label: "Notes" },
    ],
    address: addressFlags("Customer", VAT_WARNING),
  },
  {
    title: "Payment",
    flags: [
      { flag: "showBankAccountName", label: "Account name" },
      { flag: "showSortCode", label: "Sort code" },
      { flag: "showRoutingNumber", label: "Routing number" },
      { flag: "showAccountNumber", label: "Account number" },
      { flag: "showIban", label: "IBAN" },
      { flag: "showBicSwift", label: "BIC/SWIFT" },
      { flag: "showPaymentQrCode", label: "QR code and payment link" },
    ],
  },
];

const ALL_FLAGS = GROUPS.flatMap((g) => [...g.flags, ...(g.address ?? [])].map((f) => f.flag));

// Client-level: applies to every organisation's invoice PDFs. Each organisation still picks its own template,
// so the tabs here only switch the preview.
export function InvoicePdfSettingsSection() {
  const { activeClient, activeClientId } = useAuth();
  const isAdmin = activeClient?.role === "Owner" || activeClient?.role === "Admin";

  const [options, setOptions] = useState<Options | null>(null);
  const [saved, setSaved] = useState<Options | null>(null);
  const [template, setTemplate] = useState<Template>("Classic");
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!activeClientId) return;
    let cancelled = false;
    setLoadFailed(false);
    api
      .GET("/v1/settings/ar", {})
      .then(({ data }) => {
        if (cancelled) return;
        if (!data) {
          setLoadFailed(true);
          return;
        }
        setOptions(data.pdfDisplayOptions);
        setSaved(data.pdfDisplayOptions);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [activeClientId]);

  const dirty = !!options && !!saved && ALL_FLAGS.some((f) => options[f] !== saved[f]);

  async function save() {
    if (!options || !dirty) return;
    setSaving(true);
    setError(null);
    const { data, error: apiError } = await api.PATCH("/v1/settings/ar", {
      body: { pdfDisplayOptions: options },
    });
    setSaving(false);
    if (apiError || !data) {
      setError((apiError as { detail?: string } | undefined)?.detail ?? "Failed to save changes.");
      return;
    }
    setOptions(data.pdfDisplayOptions);
    setSaved(data.pdfDisplayOptions);
    toast.success("Invoice PDF settings updated");
  }

  function discard() {
    setOptions(saved);
    setError(null);
  }

  useSaveBarSection({ dirty, saving, save, discard });

  function renderFlag({ flag, label, warnWhenOff }: FlagItem) {
    if (!options || !saved) return null;
    const id = `pdf-${flag}`;
    return (
      <div key={flag} className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-4">
          <Label htmlFor={id} className="font-normal">
            {label}
          </Label>
          <Switch
            id={id}
            checked={options[flag]}
            disabled={!isAdmin || saving}
            onCheckedChange={(checked) => setOptions({ ...options, [flag]: checked })}
            className={cn(options[flag] !== saved[flag] && dirtyRing)}
          />
        </div>
        {warnWhenOff && !options[flag] && (
          <p className="text-xs text-amber-700 dark:text-amber-400">{warnWhenOff}</p>
        )}
      </div>
    );
  }

  if (loadFailed) {
    return (
      <Card>
        <CardContent className="py-12 text-sm text-destructive">Could not load settings.</CardContent>
      </Card>
    );
  }

  if (!options || !saved) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-12 text-sm text-muted-foreground">
          <Spinner className="size-4" /> Loading invoice PDF settings…
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invoice PDF</CardTitle>
        <CardDescription>
          Choose what appears on invoice PDFs. Applies to every organisation's invoices, including ones already
          sent. Each organisation still chooses its own template.
          {!isAdmin && " You need Admin or Owner role to edit these."}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <div className="grid gap-6 sm:grid-cols-3">
          {GROUPS.map((group) => (
            <fieldset key={group.title} className="flex flex-col gap-2.5">
              <legend className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {group.title}
              </legend>
              {group.flags.map((item) => renderFlag(item))}
              {group.address && (
                <>
                  <p className="mt-2 text-xs font-medium text-muted-foreground">Address</p>
                  {group.address.map((item) => renderFlag(item))}
                </>
              )}
            </fieldset>
          ))}
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex min-w-0 flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Tabs value={template} onValueChange={(v) => setTemplate(v as Template)}>
              <TabsList>
                {TEMPLATES.map((t) => (
                  <TabsTrigger key={t} value={t}>
                    {t}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <span className="text-xs text-muted-foreground">Preview with sample data</span>
          </div>
          <PdfPreview template={template} options={options} />
        </div>
      </CardContent>
    </Card>
  );
}

function PdfPreview({ template, options }: { template: Template; options: Options }) {
  // Debounced separately: a fresh { template, options } object each render would refetch in a loop.
  const debouncedTemplate = useDebouncedValue(template, 400);
  const debouncedOptions = useDebouncedValue(options, 400);
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    api
      .POST("/v1/settings/ar/pdf-preview", {
        body: { pdfTemplate: debouncedTemplate, pdfDisplayOptions: debouncedOptions },
        parseAs: "blob",
      })
      .then(({ data, response }) => {
        if (cancelled) return;
        if (!response.ok || !(data instanceof Blob)) {
          setError(true);
          return;
        }
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = URL.createObjectURL(data);
        setUrl(urlRef.current);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedTemplate, debouncedOptions, attempt]);

  useEffect(
    () => () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    [],
  );

  return (
    <div className="flex flex-col gap-2">
    <div className="relative aspect-[210/297] w-full overflow-hidden rounded-md border bg-muted/40">
      {error ? (
        <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
          <p role="alert" className="text-sm text-destructive">
            The preview could not be loaded.
          </p>
          <Button type="button" variant="outline" size="sm" onClick={() => setAttempt((n) => n + 1)}>
            Retry
          </Button>
        </div>
      ) : (
        url && (
          <iframe
            title={`${debouncedTemplate} invoice PDF preview`}
            src={`${url}#view=FitH&toolbar=0`}
            className="h-full w-full bg-white"
          />
        )
      )}
      {loading && !error && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/50">
          <Spinner className="size-5" />
        </div>
      )}
    </div>
      {url && !error && (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="self-end text-xs text-muted-foreground underline-offset-4 hover:underline"
        >
          Open full size
        </a>
      )}
    </div>
  );
}
